import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout, logoutUser } from '../../store/slices/authSlice';
import { setLoading, setSuccess } from '../../store/slices/uiSlice';
import { resetAuthenticatedCart } from '../../store/slices/cartSlice';
import { resetWishlist } from '../../store/slices/wishlistSlice';
import CartDropdown from '../cart/cartDropdown';

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector(state => state.auth);
  const { itemsCount: wishlistItemsCount } = useSelector(state => state.wishlist);

  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [materialsDropdownOpen, setMaterialsDropdownOpen] = useState(false);
  const categoryDropdownRef = useRef(null);
  const materialsDropdownRef = useRef(null);

  // Handle clicks outside to close dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setCategoryDropdownOpen(false);
      }
      if (materialsDropdownRef.current && !materialsDropdownRef.current.contains(event.target)) {
        setMaterialsDropdownOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    
    // Trim whitespace and check length
    const trimmedQuery = searchQuery.trim();
    
    if (trimmedQuery.length < 2) {
      alert('Please enter at least 2 characters to search');
      return;
    }
    
    console.log('Searching for:', trimmedQuery);
    
    // Close mobile menu if open
    setMobileMenuOpen(false);
    
    // Clear search query after navigation (optional)
    // setSearchQuery('');
    
    // Navigate to search page
    navigate(`/search?q=${encodeURIComponent(trimmedQuery)}`);
  };

  const handleLogout = async () => {
    try {
      dispatch(setLoading(true));
      
      // Call the logout API via the Redux thunk
      await dispatch(logoutUser()).unwrap();
      
      // Explicitly reset cart and wishlist
      dispatch(resetAuthenticatedCart());
      dispatch(resetWishlist());
      
      // Show success message
      dispatch(setSuccess('Logged out successfully'));
      
      // Navigate to login
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      
      // If API call fails, still reset local state
      dispatch(logout());
      dispatch(resetAuthenticatedCart());
      dispatch(resetWishlist());
      
      dispatch(setSuccess('Logged out from device'));
      navigate('/');
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Main product categories
  const mainCategories = [
    { name: "Agriculture Products", path: "/category/AGRICULTURE_PRODUCTS" },
    { name: "Packaging Products", path: "/category/PACKAGING_PRODUCTS" }
  ];

  // Agriculture subcategories
  const agricultureSubcategories = [
    { name: "Mulching Film", path: "/category/AGRICULTURE_PRODUCTS?subCategory=MULCHING_FILM" }
  ];

  // Packaging subcategories
  const packagingSubcategories = [
    { name: "Courier Bags", path: "/category/PACKAGING_PRODUCTS?subCategory=COURIER_BAGS" },
    { name: "  ↳ POD Bags", path: "/category/PACKAGING_PRODUCTS?subCategory=COURIER_BAGS&courierBagType=POD" },
    { name: "  ↳ Non-POD Bags", path: "/category/PACKAGING_PRODUCTS?subCategory=COURIER_BAGS&courierBagType=NON_POD" },
    { name: "Paper Bags", path: "/category/PACKAGING_PRODUCTS?subCategory=PAPER_BAGS" },
    { name: "  ↳ SOS Paper Bags (White)", path: "/category/PACKAGING_PRODUCTS?subCategory=PAPER_BAGS&paperBagType=SOS_PAPER_BAGS_WHITE" },
    { name: "  ↳ SOS Paper Bags (Brown)", path: "/category/PACKAGING_PRODUCTS?subCategory=PAPER_BAGS&paperBagType=SOS_PAPER_BAGS_BROWN" },
    { name: "  ↳ Twisted Handle (Brown)", path: "/category/PACKAGING_PRODUCTS?subCategory=PAPER_BAGS&paperBagType=TWISTED_HANDLE_BROWN" },
    { name: "  ↳ Twisted Handle (White)", path: "/category/PACKAGING_PRODUCTS?subCategory=PAPER_BAGS&paperBagType=TWISTED_HANDLE_WHITE" },
  ];

  // Materials for filtering
 const materials = [
    { name: "LLDPE", path: "/products?material=LLDPE", description: "Linear Low Density Polyethylene" },
    { name: "LDPE", path: "/products?material=LDPE", description: "Low Density Polyethylene" },
    { name: "Kraft Paper", path: "/products?material=KRAFT_PAPER" },
    { name: "Bleached Paper", path: "/products?material=BLEACHED_PAPER" },
  ];

  return (
    <header className="w-full">
      {/* Top Promotion Banner */}
      <div className="bg-green-600 text-white py-3 px-4 text-center text-sm font-medium">
        🌾 Premium Agriculture & Packaging Solutions ✨
      </div>
      
      {/* Main Navbar */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
           <Link to="/" className="flex items-center gap-2">
             <img 
               src="/assets/logo/klm-logo.png" 
               alt="KLM Logo" 
               className="h-14 w-auto"
             />
             <span
               className="text-4xl font-extrabold tracking-wide"
               style={{ color: '#D0001B', fontFamily: 'Arial Black, Arial, sans-serif', letterSpacing: '0.04em' }}
             >
               KLMKART
             </span>
           </Link>
            
            {/* Search Bar */}
            <div className="hidden md:flex flex-1 max-w-xl mx-6">
              <form onSubmit={handleSearch} className="w-full relative">
                <input
                  type="text"
                  placeholder="Search for mulching film, grow bags, packaging..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  minLength={2}
                />
                <button 
                  type="submit"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:text-green-600 transition"
                  disabled={searchQuery.trim().length < 2}
                  title={searchQuery.trim().length < 2 ? 'Enter at least 2 characters' : 'Search'}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${searchQuery.trim().length < 2 ? 'text-gray-300' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </form>
            </div>
            
            {/* Navigation Icons */}
            <div className="flex items-center space-x-6">
              <div className="hidden md:flex items-center space-x-6">
                <Link to={isAuthenticated ? "/profile" : "/login"} className="flex flex-col items-center text-gray-700 hover:text-green-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="text-xs mt-1">
                    {isAuthenticated ? 'ACCOUNT' : 'SIGN IN'}
                  </span>
                </Link>
                
                <Link to="/orders" className="flex flex-col items-center text-gray-700 hover:text-green-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  <span className="text-xs mt-1">ORDERS</span>
                </Link>
                
                <Link to="/wishlist" className="flex flex-col items-center text-gray-700 hover:text-green-600 relative">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  <span className="text-xs mt-1">WISHLIST</span>
                  {wishlistItemsCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {wishlistItemsCount}
                    </span>
                  )}
                </Link>
                
                {/* Cart Dropdown */}
                <CartDropdown />
                
                {/* Logout Button - Desktop */}
                {isAuthenticated && (
                  <button 
                    onClick={handleLogout}
                    className="flex flex-col items-center text-red-600 hover:text-red-700"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span className="text-xs mt-1">LOGOUT</span>
                  </button>
                )}
              </div>
              
              <div className="flex items-center md:hidden">
                {/* Logout button for mobile */}
                {isAuthenticated && (
                  <button 
                    onClick={handleLogout}
                    className="flex items-center text-red-600 mr-4"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </button>
                )}
                
                {/* Mobile menu button */}
                <button 
                  className="flex items-center text-gray-700"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          
          {/* Mobile Search */}
          <div className="mt-3 md:hidden">
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                placeholder="Search products..."
                className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                minLength={2}
              />
              <button 
                type="submit"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:text-green-600 transition"
                disabled={searchQuery.trim().length < 2}
                title={searchQuery.trim().length < 2 ? 'Enter at least 2 characters' : 'Search'}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${searchQuery.trim().length < 2 ? 'text-gray-300' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </form>
          </div>
        </div>
      </div>
      
      {/* Secondary Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="container mx-auto px-4">
          <div className="hidden md:flex items-center space-x-8 py-3">
            <Link to="/products" className="flex items-center text-green-600 hover:text-green-700 font-medium">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
              All Products
            </Link>
            
            {/* Categories Dropdown */}
            <div className="relative" ref={categoryDropdownRef}>
              <button 
                className="flex items-center text-gray-800 hover:text-green-600"
                onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
              >
                Shop By Category
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ml-1 transition-transform ${categoryDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {categoryDropdownOpen && (
                <div className="absolute z-10 w-80 mt-2 bg-white border border-gray-200 rounded-md shadow-lg">
                  <div className="py-2">
                    {/* Main Categories */}
                    {mainCategories.map((category, index) => (
                      <Link 
                        key={index}
                        to={category.path} 
                        className="block px-4 py-2 text-gray-900 font-semibold hover:bg-green-50 hover:text-green-600"
                        onClick={() => setCategoryDropdownOpen(false)}
                      >
                        {category.name}
                      </Link>
                    ))}
                    
                    {/* Agriculture Subcategories */}
                    <div className="border-t border-gray-200 mt-2 pt-2">
                      <p className="px-4 py-1 text-xs font-semibold text-gray-500 uppercase">Agriculture</p>
                      {agricultureSubcategories.map((subcat, index) => (
                        <Link 
                          key={index}
                          to={subcat.path} 
                          className="block px-4 py-2 pl-8 text-sm text-gray-800 hover:bg-green-50 hover:text-green-600"
                          onClick={() => setCategoryDropdownOpen(false)}
                        >
                          {subcat.name}
                        </Link>
                      ))}
                    </div>
                    
                    {/* Packaging Subcategories */}
                    <div className="border-t border-gray-200 mt-2 pt-2">
                      <p className="px-4 py-1 text-xs font-semibold text-gray-500 uppercase">Packaging</p>
                      {packagingSubcategories.map((subcat, index) => (
                        <Link 
                          key={index}
                          to={subcat.path} 
                          className="block px-4 py-2 pl-8 text-sm text-gray-800 hover:bg-green-50 hover:text-green-600"
                          onClick={() => setCategoryDropdownOpen(false)}
                        >
                          {subcat.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Materials Dropdown */}
            {/* <div className="relative" ref={materialsDropdownRef}>
              <button 
                className="flex items-center text-gray-800 hover:text-green-600"
                onClick={() => setMaterialsDropdownOpen(!materialsDropdownOpen)}
              >
                Shop By Material
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ml-1 transition-transform ${materialsDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {materialsDropdownOpen && (
                <div className="absolute z-10 w-72 mt-2 bg-white border border-gray-200 rounded-md shadow-lg">
                  <div className="py-2">
                    {materials.map((material, index) => (
                      <Link 
                        key={index}
                        to={material.path} 
                        className="block px-4 py-2 text-gray-800 hover:bg-green-50 hover:text-green-600"
                        onClick={() => setMaterialsDropdownOpen(false)}
                      >
                        <div className="font-medium">{material.name}</div>
                        {material.description && (
                          <div className="text-xs text-gray-500">{material.description}</div>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div> */}
            
            <Link to="/products?isEcoFriendly=true" className="text-gray-800 hover:text-green-600">
              <span className="flex items-center">
                <span className="mr-1">🌱</span> Eco-Friendly
              </span>
            </Link>
          </div>
        </div>
      </nav>
      
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="py-3 px-4 space-y-4">
            <Link to={isAuthenticated ? "/profile" : "/login"} className="flex items-center py-2 text-gray-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {isAuthenticated ? `My Account (${user?.name})` : 'Sign In / Register'}
            </Link>
            
            <Link to="/orders" className="flex items-center py-2 text-gray-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
              My Orders
            </Link>
            
            <Link to="/wishlist" className="flex items-center py-2 text-gray-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              Wishlist
            </Link>
            
            <Link to="/cart" className="flex items-center py-2 text-gray-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              Cart
            </Link>
            
            <Link to="/products" className="flex items-center py-2 text-green-600">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
              All Products
            </Link>
            
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h3 className="font-medium text-gray-900 mb-2">Main Categories</h3>
              <div className="space-y-2 pl-2">
                {mainCategories.map((category, index) => (
                  <Link
                    key={index}
                    to={category.path}
                    className="block text-gray-700 py-1 hover:text-green-600 font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {category.name}
                  </Link>
                ))}
              </div>
            </div>
            
            <div className="border-t border-gray-200 pt-4">
              <h3 className="font-medium text-gray-900 mb-2">Agriculture Products</h3>
              <div className="space-y-2 pl-2">
                {agricultureSubcategories.map((subcat, index) => (
                  <Link
                    key={index}
                    to={subcat.path}
                    className="block text-gray-700 py-1 hover:text-green-600 text-sm"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {subcat.name}
                  </Link>
                ))}
              </div>
            </div>
            
            <div className="border-t border-gray-200 pt-4">
              <h3 className="font-medium text-gray-900 mb-2">Packaging Products</h3>
              <div className="space-y-2 pl-2">
                {packagingSubcategories.map((subcat, index) => (
                  <Link
                    key={index}
                    to={subcat.path}
                    className="block text-gray-700 py-1 hover:text-green-600 text-sm"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {subcat.name}
                  </Link>
                ))}
              </div>
            </div>
            
            <div className="border-t border-gray-200 pt-4">
              <h3 className="font-medium text-gray-900 mb-2">Shop By Material</h3>
              <div className="space-y-2 pl-2">
                {materials.map((material, index) => (
                  <Link
                    key={index}
                    to={material.path}
                    className="block text-gray-700 py-1 hover:text-green-600 text-sm"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {material.name}
                  </Link>
                ))}
              </div>
            </div>
            
            <div className="border-t border-gray-200 pt-4">
              <Link to="/products?isEcoFriendly=true" className="flex items-center py-2 text-gray-700">
                <span className="mr-3">🌱</span> Eco-Friendly Products
              </Link>
            </div>
            
            {/* Logout in mobile menu */}
            {isAuthenticated && (
              <div className="border-t border-gray-200 pt-4">
                <button 
                  onClick={handleLogout}
                  className="flex items-center py-2 text-red-600"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;